import React, { useEffect, useState } from 'react';
import { fetchAgentPerformance, AgentPerformanceResult } from '../services/MendsVisionService';

const PERIODS = [
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
  { id: '90d', label: '90 dias' },
];

/** Colunas da tabela; o rótulo também aparece em cada linha do cartão no celular. */
const COLUMNS = [
  'Funcionário',
  'Sugestões',
  'Aprovadas como vieram',
  'Aprovadas com edição',
  'Rejeitadas',
  'Venceram sem decisão',
  'Taxa de aprovação',
  'Mensagens aprovadas',
  'Cliente respondeu',
] as const;

const pct = (v: number | null) => (v == null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);

/**
 * Desempenho de cada funcionário digital no que ele propõe à equipe: quanto foi aprovado como veio, aprovado depois
 * de editar, rejeitado, e quantas mensagens aprovadas tiveram resposta do cliente.
 */
export const AgentPerformance: React.FC<{
  sectorId: number;
  agentNames: Record<number, string>;
  onOpenAgent?: (agentId: number) => void;
}> = ({ sectorId, agentNames, onOpenAgent }) => {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState<AgentPerformanceResult | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error'>('loading');

  useEffect(() => {
    if (!sectorId) return undefined;
    let alive = true;
    setState('loading');
    fetchAgentPerformance(sectorId, period)
      .then((d) => { if (alive) { setData(d); setState('idle'); } })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, [sectorId, period]);

  return (
    <section className="ai-hub-health mai-agent-perf" aria-label="Desempenho dos funcionários">
      <div className="ai-hub-roster__head">
        <h3>Desempenho nas sugestões</h3>
        <div className="mai-agent-perf-periods" role="group" aria-label="Período">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`mai-chip ${period === p.id ? 'is-active' : ''}`}
              aria-pressed={period === p.id}
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {state === 'loading' && <p className="ai-hub-roster__empty">Carregando o desempenho…</p>}
      {state === 'error' && <p className="ai-hub-roster__empty" role="alert">Não foi possível carregar o desempenho agora.</p>}
      {state === 'idle' && data && data.agents.length === 0 && (
        <p className="ai-hub-roster__empty">Nenhum funcionário fez sugestões neste período.</p>
      )}
      {state === 'idle' && data && data.agents.length > 0 && (
        <div className="mai-agent-perf-table-wrap">
          <table className="mai-agent-perf-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => <th key={c} scope="col">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.agents.map((a) => (
                <tr key={a.agentId}>
                  <td className="mai-agent-perf-name" data-label={COLUMNS[0]}>
                    {onOpenAgent ? (
                      <button type="button" className="mai-linkish" onClick={() => onOpenAgent(a.agentId)}>
                        {agentNames[a.agentId] || `Funcionário #${a.agentId}`}
                      </button>
                    ) : (agentNames[a.agentId] || `Funcionário #${a.agentId}`)}
                  </td>
                  <td data-label={COLUMNS[1]}>{a.proposed}</td>
                  <td data-label={COLUMNS[2]}>{a.approvedAsIs}</td>
                  <td data-label={COLUMNS[3]}>{a.approvedEdited}</td>
                  <td data-label={COLUMNS[4]}>{a.rejected}</td>
                  <td data-label={COLUMNS[5]}>{a.expired}</td>
                  <td data-label={COLUMNS[6]}><strong>{pct(a.approvalRate)}</strong></td>
                  <td data-label={COLUMNS[7]}>{a.messagesApproved}</td>
                  <td data-label={COLUMNS[8]}>{a.messagesApproved > 0 ? `${a.messagesReplied} (${pct(a.replyRate)})` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.howCalculated && (
        <details className="mai-op-note-fold">
          <summary>Como é calculado</summary>
          <p className="mai-card-detail mai-op-note">{data.howCalculated}</p>
        </details>
      )}
    </section>
  );
};

export default AgentPerformance;
